import { Api } from '../../redux/api/axiosInstance';
import type { AxiosResponse } from 'axios';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface OfferComponentInput {
  componentTypeId?: number | null;
  label: string;
  amount: number;
}

export interface OfferBenefitInput {
  label: string;
  description?: string;
}

export interface CreateOfferPayload {
  candidate_id: number;
  /** Required by OffersController's CreateOfferDto (PHASE-2-CONTRACTS.md §4) — an offer is
   *  raised FROM a job, never beside one. position_title/grade are accepted but ignored on
   *  write; the service always overwrites them from the job. */
  job_id: number;
  position_title?: string;
  salary_amount?: number;
  currency?: string;
  pay_frequency?: string;
  grade?: string;
  joining_date?: string;
  valid_till?: string;
  terms?: string;
  components?: OfferComponentInput[];
  benefits?: OfferBenefitInput[];
}

export interface OfferSalaryComponent {
  component_id: number;
  offer_id: number;
  component_type_id: number | null;
  label: string;
  amount: number;
  sort_order: number;
}

export interface OfferBenefit {
  benefit_id: number;
  offer_id: number;
  label: string;
  description: string | null;
  sort_order: number;
}

export type OfferStatus =
  | 'Draft'
  | 'Pending Approval'
  | 'Approved'
  | 'Sent'
  | 'Viewed'
  | 'Accepted'
  | 'Declined'
  | 'Withdrawn'
  | 'On Hold'
  | 'Rejected';

/** Statuses OffersController's ALLOWED_TRANSITIONS treats as "still in play" — not yet a
 *  terminal outcome. Mirrors the same list in `src/utils/offerLogic.ts`. */
export const LIVE_OFFER_STATUSES: readonly OfferStatus[] = [
  'Draft', 'Pending Approval', 'Approved', 'Sent', 'Viewed',
];

export interface CandidateOffer {
  offer_id: number;
  candidate_id: number;
  job_id: number | null;
  OrganizationID: number;
  version: number;
  status: OfferStatus;
  position_title: string | null;
  salary_amount: number | null;
  currency: string;
  pay_frequency: string;
  grade: string | null;
  joining_date: string | null;
  valid_till: string | null;
  terms: string | null;
  decline_reason: string | null;
  decline_action: string | null;
  created_by: number | null;
  approved_by: number | null;
  sent_at: string | null;
  viewed_at: string | null;
  responded_at: string | null;
  pdf_url: string | null;
  salary_in_range: boolean;
  created_at: string;
  updated_at: string;
  components?: OfferSalaryComponent[];
  benefits?: OfferBenefit[];
  // ATS migration 031 — snapshot of the job's structural fields at the moment the offer was
  // raised (Jobs.department_id/designation_id/cost_centre added by migration 030). A copy, not
  // a live join.
  department_id?: number | null;
  designation_id?: number | null;
  cost_centre?: string | null;
  withdrawn_reason?: string | null;
  withdrawn_at?: string | null;
  superseded_by_offer_id?: number | null;
  /** 'pending' | 'delivered' | 'failed' | null — set on send; nothing yet flips it to
   *  'delivered' (see PHASE-2-CONTRACTS.md §4's "Sent must not claim delivery"). */
  delivery_status?: 'pending' | 'delivered' | 'failed' | null;
}

/** createOffer's response carries an optional warning — set when the candidate already holds a
 *  live offer for a DIFFERENT job (not blocking; sending this one will withdraw the other). */
export type CreateOfferResult = CandidateOffer & { warning?: string };

export interface OfferActivityLog {
  log_id: number;
  offer_id: number;
  actor_id: number | null;
  action: string | null;
  from_status: string | null;
  to_status: string | null;
  notes: string | null;
  created_at: string;
}

export interface DeclineOfferPayload {
  decline_reason?: string;
  decline_action: 'counter_offer' | 'extend_deadline' | 'reject' | 'on_hold';
  new_valid_till?: string;
}

// ── API calls ─────────────────────────────────────────────────────────────────
// NOTE: All responses are wrapped by TransformInterceptor as:
//   { success: boolean; statusCode: number; data: T; timestamp: string }
// So axios r.data = envelope → we need r.data.data to get the actual payload.

type Envelope<T> = { success: boolean; data: T; statusCode: number; timestamp: string };

/** Unwrap a single-object response from the TransformInterceptor envelope. */
const unwrap = <T>(r: AxiosResponse<Envelope<T> | T>): T => {
  const body = r.data;
  if (body !== null && typeof body === 'object' && 'success' in body && 'data' in body) {
    return (body as Envelope<T>).data;
  }
  return body as T;
};

/** Unwrap an array response from the TransformInterceptor envelope. */
const unwrapArray = <T>(r: AxiosResponse<Envelope<T[]> | T[]>): T[] => {
  const body = r.data;
  let d: unknown;
  if (body !== null && typeof body === 'object' && 'success' in body && 'data' in body) {
    d = (body as Envelope<T[]>).data;
  } else {
    d = body;
  }
  return Array.isArray(d) ? d : [];
};

export const createOffer = (payload: CreateOfferPayload): Promise<CreateOfferResult> =>
  Api.post('/offers', payload).then((r: AxiosResponse<any>) => unwrap<CreateOfferResult>(r));

export const getOffersByCandidate = (candidateId: number): Promise<CandidateOffer[]> =>
  Api.get(`/offers/candidate/${candidateId}`).then((r: AxiosResponse<any>) => unwrapArray<CandidateOffer>(r));

export const getOfferById = (offerId: number): Promise<CandidateOffer> =>
  Api.get(`/offers/${offerId}`).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

/** Rendered offer-letter HTML for the recruiter preview (same render the candidate gets). */
export const getOfferPreview = (offerId: number): Promise<{ html: string }> =>
  Api.get(`/offers/${offerId}/preview`).then((r: AxiosResponse<any>) => unwrap<{ html: string }>(r));

export const getOfferHistory = (offerId: number): Promise<OfferActivityLog[]> =>
  Api.get(`/offers/${offerId}/history`).then((r: AxiosResponse<any>) => unwrapArray<OfferActivityLog>(r));

export const submitForApproval = (offerId: number): Promise<CandidateOffer> =>
  Api.patch(`/offers/${offerId}/submit`).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

export const approveOffer = (offerId: number): Promise<CandidateOffer> =>
  Api.patch(`/offers/${offerId}/approve`).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

export const rejectApproval = (offerId: number, comments?: string): Promise<CandidateOffer> =>
  Api.patch(`/offers/${offerId}/reject-approval`, { comments }).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

export const sendOffer = (
  offerId: number,
  opts?: { pdfTemplateId?: number; emailTemplateId?: number },
): Promise<CandidateOffer> =>
  Api.patch(`/offers/${offerId}/send`, opts ?? {}).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

/** Download the filled offer-letter PDF for a chosen template. */
export const downloadOfferLetter = async (
  offerId: number,
  templateId: number,
  candidateName?: string,
): Promise<void> => {
  const res = await Api.get(`/offers/${offerId}/letter.pdf?templateId=${templateId}`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Offer_Letter_${(candidateName || 'Candidate').replace(/\s+/g, '_')}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const acceptOffer = (offerId: number): Promise<CandidateOffer> =>
  Api.patch(`/offers/${offerId}/accept`).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

export const declineOffer = (
  offerId: number,
  payload: DeclineOfferPayload,
): Promise<{ success: boolean; message: string; newOfferId?: number }> =>
  Api.patch(`/offers/${offerId}/decline`, payload).then(
    (r: AxiosResponse<any>) => unwrap<{ success: boolean; message: string; newOfferId?: number }>(r),
  );

// ── Offers register (screen design §9, PHASE-2-CONTRACTS.md §4) ────────────────────────────────

export interface OfferRegisterRow {
  offer_id: number;
  candidate_id: number;
  candidate_name: string;
  job_id: number | null;
  position_title: string | null;
  salary_amount: number | null;
  currency: string;
  status: OfferStatus;
  effective_status: string;
  delivery_status: 'pending' | 'delivered' | 'failed' | null;
  valid_till: string | null;
  /** When the candidate opened it. Proof of delivery in a way `delivery_status` never is. */
  viewed_at: string | null;
  approved_unsent_hours: number | null;
  expires_in_days: number | null;
  sent_unopened_days: number | null;
  opened_days_ago: number | null;
  age_days: number | null;
  bucket: string;
}

export interface OfferRegisterResponse {
  tab: string;
  counts: Record<string, number>;
  items: OfferRegisterRow[];
}

/** `GET /offers/register` — a read model over CandidateOffers, tabbed and aged server-side.
 *  `tab` omitted resolves to 'needs_attention'. */
/** Sort keys the register accepts (ATS allow-list, `offer-register-sort.util.ts`). */
export type OfferRegisterSortKey = 'updated' | 'candidate' | 'position' | 'amount' | 'valid_till' | 'age';

/**
 * `controls` is optional and additive: search narrows every tab (and the tab counts) to
 * candidate name / position, sort orders the tab's rows server-side. Omitted = today's
 * register, most recently updated first.
 */
export const getOffersRegister = (
  tab?: string,
  controls: { search?: string; sort_by?: OfferRegisterSortKey; sort_order?: 'asc' | 'desc' } = {},
): Promise<OfferRegisterResponse> =>
  Api.get('/offers/register', {
    params: {
      ...(tab ? { tab } : {}),
      ...(controls.search ? { search: controls.search } : {}),
      ...(controls.sort_by ? { sort_by: controls.sort_by, sort_order: controls.sort_order } : {}),
    },
  }).then((r: AxiosResponse<any>) => unwrap<OfferRegisterResponse>(r));

/** Terminal, mandatory reason — also the mechanism `sendOffer` uses internally to supersede a
 *  candidate's other live offers. */
export const withdrawOffer = (offerId: number, reason: string): Promise<CandidateOffer> =>
  Api.patch(`/offers/${offerId}/withdraw`, { reason }).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

/** Pushes a live (or just-expired) offer's `valid_till` forward without creating a new draft
 *  version — the "Extend" action in the offers register. */
export const extendOfferValidity = (offerId: number, newValidTill: string): Promise<CandidateOffer> =>
  Api.patch(`/offers/${offerId}/extend-validity`, { new_valid_till: newValidTill }).then(
    (r: AxiosResponse<any>) => unwrap<CandidateOffer>(r),
  );

/** Org-level switch: send an approved offer automatically rather than leaving it in the
 *  approved-not-sent state the register's "Needs attention" tab exists to surface. Default off. */
export const getSendOnApprovalSetting = (): Promise<{ enabled: boolean }> =>
  Api.get('/offers/settings/send-on-approval').then((r: AxiosResponse<any>) => unwrap<{ enabled: boolean }>(r));

export const setSendOnApprovalSetting = (enabled: boolean): Promise<{ enabled: boolean }> =>
  Api.patch('/offers/settings/send-on-approval', { enabled }).then(
    (r: AxiosResponse<any>) => unwrap<{ enabled: boolean }>(r),
  );
